import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import RefreshingIndicator from "./RefreshingIndicator";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Labels from "../labels/Labels";

export default function MemberStatements(props) {
  const { memberInfo } = props;
  const [downloadingId, setDownloadingId] = useState(null);

  const getMonthName = (monthNumber) => {
    const date = new Date(2020, Number(monthNumber) - 1);
    return date.toLocaleString("en-US", { month: "long" });
  };

  async function fetchStatements() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/member/statements?memberId=` +
        memberInfo.memberId +
        `&companyId=` +
        memberInfo.companyId
    );
    return response;
  }

  const {
    data: dataStatements,
    isError: isErrorStatements,
    isFetching: isFetchingStatements,
    isLoading: isLoadingStatements,
  } = useQuery(
    ["memberStatements", memberInfo.memberId, memberInfo.companyId],
    fetchStatements,
    {
      keepPreviousData: true,
      enabled: !!memberInfo?.memberId,
    }
  );

  const handleDownloadClick = (idStatementLine, filename) => {
    setDownloadingId(idStatementLine);
    HttpService.getAxiosClient()
      .get(
        window.API_URL +
          `/member/statements/` +
          idStatementLine +
          `?memberId=` +
          memberInfo.memberId +
          `&companyId=` +
          memberInfo.companyId,
        { responseType: "blob" }
      )
      .then((response) => {
        setDownloadingId(null);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || idStatementLine + ".pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => {
        setDownloadingId(null);
      });
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return;
    props.setOpen(false);
  };

  const statements = dataStatements?.data?.statementsList || [];

  const getStatusChipColor = (status) => {
    if (status?.toUpperCase() === "PROCESSED") return "success";
    return "warning";
  };

  const getProcessorLabel = (location) => {
    if (!location) return null;
    return /^\d+$/.test(location.trim()) ? "visadps" : "qolo";
  };

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      open={props.open}
      onClose={handleClose}
      disableEscapeKeyDown={true}
    >
      <Box component="div">
        {/* Title bar consistent with all other paycard-admin dialogs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="div" color="white">
              {Labels.getLabel("members.viewStatements", "Member Statements")}
            </Typography>
            <DescriptionIcon sx={{ color: "white" }} />
            {memberInfo?.firstName && (
              <Typography
                variant="subtitle2"
                component="div"
                color="white"
                sx={{ opacity: 0.85, ml: 1 }}
              >
                — {memberInfo.firstName} {memberInfo.lastName} (
                {memberInfo.memberId})
              </Typography>
            )}
          </Box>
          <Box>
            <IconButton
              edge="end"
              color="inherit"
              size="small"
              onClick={() => props.setOpen(false)}
              aria-label="close"
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2 }}>
          {isLoadingStatements ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          ) : isErrorStatements ? (
            <Box sx={{ p: 2 }}>
              <Typography color="error">
                {Labels.getLabel(
                  "members.statementsError",
                  "There was an error loading the statements. Please try again later."
                )}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ minHeight: "1.5rem", mb: 1 }}>
                {isFetchingStatements && <RefreshingIndicator />}
              </Box>

              {statements.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "150px",
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {Labels.getLabel(
                      "members.noStatements",
                      "No statements available for this member."
                    )}
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={1}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "grey.100" }}>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {Labels.getLabel(
                            "members.statements.period",
                            "Period"
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {Labels.getLabel("members.statements.name", "Name")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {Labels.getLabel(
                            "members.statements.periodDates",
                            "Period Dates"
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {Labels.getLabel(
                            "members.statements.status",
                            "Status"
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {Labels.getLabel(
                            "members.statements.createdAt",
                            "Created At"
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {Labels.getLabel(
                            "members.statements.download",
                            "Download"
                          )}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statements.map((statement) => (
                        <TableRow
                          key={statement.idStatementLine}
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {getMonthName(statement.month)}{" "}
                              {Number(statement.year).toFixed(0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {getProcessorLabel(statement.location) && (
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: "bold",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                {getProcessorLabel(statement.location)}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {statement.name || "—"}
                            </Typography>
                            {statement.brand && (
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {statement.brand}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: "text.secondary" }}
                            >
                              {statement.periodStartDate
                                ? statement.periodStartDate.slice(0, 10)
                                : "—"}
                              {" → "}
                              {statement.periodEndDate
                                ? statement.periodEndDate.slice(0, 10)
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statement.status || "—"}
                              color={getStatusChipColor(statement.status)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {statement.creationDate
                                ? statement.creationDate.slice(0, 10)
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {statement.status?.toUpperCase() ===
                              "PROCESSED" && (
                              <Tooltip
                                title={Labels.getLabel(
                                  "members.statements.downloadPdf",
                                  "Download PDF"
                                )}
                              >
                                <span>
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    disabled={
                                      downloadingId ===
                                      statement.idStatementLine
                                    }
                                    onClick={() =>
                                      handleDownloadClick(
                                        statement.idStatementLine,
                                        statement.filename
                                      )
                                    }
                                  >
                                    {downloadingId ===
                                    statement.idStatementLine ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <FileDownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
}
